import { useEffect } from 'react';
import { Link } from 'react-router';
import { CopyButton } from './ag-grid/cellRenderers';
import { returnTarget } from '../utils/returnTo';
import { getEvalSetViewUrl } from '../utils/url';
import { OpenInViewerMenu } from './OpenInMenu';

export type RunView =
  'transcripts' | 'status' | 'approvals' | 'timeline' | 'config';

/**
 * The views of one eval set. Transcripts is a plain <a> (full document load)
 * because the embedded viewer ships its own global stylesheet; the rest are
 * Hawk pages and stay in-app. Lists filtered to this run leave it, so they
 * are links beside the tabs rather than tabs; Clone is an action, not a view,
 * and sits apart.
 */
function runViews(evalSetId: string) {
  const id = encodeURIComponent(evalSetId);
  return [
    {
      key: 'transcripts',
      label: 'Transcripts',
      href: getEvalSetViewUrl(evalSetId),
      reload: true,
    },
    { key: 'status', label: 'Status', href: `/eval-set/${id}/status` },
    { key: 'approvals', label: 'Approvals', href: `/eval-set/${id}/approvals` },
    { key: 'timeline', label: 'Timeline', href: `/eval-set/${id}/timeline` },
    { key: 'config', label: 'Config', href: `/eval-set/${id}/config` },
  ] as const;
}

// No Samples link: `/meta/samples` has no eval-set filter (it drops the
// `filter_eval_set_id` the Samples grid sends), so it would list every eval
// set's samples under an eval-set filter badge.
function runLists(evalSetId: string) {
  const id = encodeURIComponent(evalSetId);
  return [
    {
      label: 'Search transcripts',
      title: 'Transcript Search, scoped to this eval set',
      href: `/search?eval_set=${id}`,
    },
  ];
}

interface RunHeaderProps {
  evalSetId: string;
  /** Which view this page is; its tab is marked current. */
  current?: RunView;
  /** An extra trailing crumb, e.g. a sample uuid on per-sample pages. */
  leaf?: string;
  /** Right-aligned content (status, freshness, owner actions). */
  children?: React.ReactNode;
}

/**
 * The pane header of every Hawk-owned run page: the list it was opened from,
 * the id itself with a copy button, the run's other views as tabs, and Clone
 * as the one action. Wraps onto a second line when the pane is narrow.
 */
export function RunHeader({
  evalSetId,
  current,
  leaf,
  children,
}: RunHeaderProps) {
  const back = returnTarget();
  const target = { level: 'eval_set', evalSetId } as const;
  useEffect(() => {
    document.title = `${leaf ?? evalSetId} · Hawk`;
  }, [evalSetId, leaf]);
  return (
    <div className="pane-header">
      <nav aria-label="Breadcrumb" className="shrink-0">
        <Link
          to={back.to}
          className="link inline-flex items-center gap-0.5 text-xs no-underline"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M10 3.5 5.5 8l4.5 4.5" />
          </svg>
          {back.label}
        </Link>
      </nav>
      <div className="flex min-w-0 max-w-full items-center gap-0.5">
        <h1 className="min-w-0 break-all font-mono text-xs font-medium leading-4 text-ink">
          {evalSetId}
        </h1>
        <CopyButton value={evalSetId} />
      </div>
      {leaf && (
        <span className="pane-meta min-w-0 break-all">
          sample <span className="font-mono text-ink-2">{leaf}</span>
        </span>
      )}
      <div className="seg flex-wrap" role="navigation" aria-label="Run views">
        {runViews(evalSetId).map(v =>
          'reload' in v ? (
            <a
              key={v.key}
              href={v.href}
              aria-current={v.key === current ? 'page' : undefined}
            >
              {v.label}
            </a>
          ) : (
            <Link
              key={v.key}
              to={v.href}
              aria-current={v.key === current ? 'page' : undefined}
            >
              {v.label}
            </Link>
          )
        )}
      </div>
      <OpenInViewerMenu target={target} subject={evalSetId} />
      <nav aria-label="This eval set in other lists" className="flex gap-3">
        {runLists(evalSetId).map(l => (
          <Link
            key={l.href}
            to={l.href}
            title={l.title}
            className="row-action row-action-accent"
          >
            {l.label}
          </Link>
        ))}
      </nav>
      <div className="pane-actions">
        {children}
        <Link
          to={`/launch?clone=${encodeURIComponent(evalSetId)}`}
          className="btn btn-ghost no-underline"
          title="Start a new eval set from this one's config"
        >
          Clone…
        </Link>
      </div>
    </div>
  );
}
